export type TextureFormat =
	| "r8unorm"
	| "r8snorm"
	| "r8uint"
	| "r8sint"
	| "r16uint"
	| "r16sint"
	| "r16float"
	| "rg8unorm"
	| "rg8snorm"
	| "rg8uint"
	| "rg8sint"
	| "r32uint"
	| "r32sint"
	| "r32float"
	| "rg16uint"
	| "rg16sint"
	| "rg16float"
	| "rgba8unorm"
	| "rgba8unorm-srgb"
	| "rgba8snorm"
	| "rgba8uint"
	| "rgba8sint"
	| "rgb9e5ufloat"
	| "rgb10a2uint"
	| "rgb10a2unorm"
	| "rg11b10ufloat"
	| "rg32uint"
	| "rg32sint"
	| "rg32float"
	| "rgba16uint"
	| "rgba16sint"
	| "rgba16float"
	| "rgba32uint"
	| "rgba32sint"
	| "rgba32float"

	// Extension formats
	| "r16unorm"
	| "rg16unorm"
	| "rgba16unorm"

	// Needs conversion from rgb to rgba
	// RGB textures are not directly supported using WebGPU
	// Example conversion: https://github.com/vsg-dev/vsgExamples/blob/master/data/shaders/RGBtoRGBA.comp
	| "rgb8unorm"
	| "rgb8unorm-srgb"
	| "rgb8snorm"
	| "rgb8uint"
	| "rgb8sint"
	| "rgb16float"
	| "rgb16uint"
	| "rgb16sint"
	| "rgb32float"
	| "rgb32uint"
	| "rgb32sint"

export function getTextureInternalFormat(
	gl: WebGL2RenderingContext,
	format: TextureFormat = "rgba8unorm",
): GLenum {
	const ext = gl.getExtension("EXT_texture_norm16")

	switch (format) {
		case "r8unorm":
			return gl.R8
		case "r8snorm":
			return gl.R8_SNORM
		case "r8uint":
			return gl.R8UI
		case "r8sint":
			return gl.R8I
		case "r16uint":
			return gl.R16UI
		case "r16sint":
			return gl.R16I
		case "r16float":
			return gl.R16F
		case "r32uint":
			return gl.R32UI
		case "r32sint":
			return gl.R32I
		case "r32float":
			return gl.R32F

		case "rg8unorm":
			return gl.RG8
		case "rg8snorm":
			return gl.RG8_SNORM
		case "rg8uint":
			return gl.RG8UI
		case "rg8sint":
			return gl.RG8I
		case "rg16uint":
			return gl.RG16UI
		case "rg16sint":
			return gl.RG16I
		case "rg16float":
			return gl.RG16F
		case "rg32uint":
			return gl.RG32UI
		case "rg32sint":
			return gl.RG32I
		case "rg32float":
			return gl.RG32F

		case "rgb8unorm":
			return gl.RGB
		case "rgb8unorm-srgb":
			return gl.SRGB8
		case "rgb8snorm":
			return gl.RGB8_SNORM
		case "rgb8uint":
			return gl.RGB8UI
		case "rgb8sint":
			return gl.RGB8I
		case "rgb16uint":
			return gl.RGB16UI
		case "rgb16sint":
			return gl.RGB16I
		case "rgb16float":
			return gl.RGB16F
		case "rgb32uint":
			return gl.RGB32UI
		case "rgb32sint":
			return gl.RGB32I
		case "rgb32float":
			return gl.RGB32F

		case "rgba8unorm":
			return gl.RGBA
		case "rgba8unorm-srgb":
			return gl.SRGB8_ALPHA8
		case "rgba8snorm":
			return gl.RGBA8_SNORM
		case "rgba8uint":
			return gl.RGBA8UI
		case "rgba8sint":
			return gl.RGBA8I
		case "rgba16uint":
			return gl.RGBA16UI
		case "rgba16sint":
			return gl.RGBA16I
		case "rgba16float":
			return gl.RGBA16F
		case "rgba32uint":
			return gl.RGBA32UI
		case "rgba32sint":
			return gl.RGBA32I
		case "rgba32float":
			return gl.RGBA32F

		case "rgb9e5ufloat":
			return gl.RGB9_E5
		case "rgb10a2uint":
			return gl.RGB10_A2UI
		case "rgb10a2unorm":
			return gl.RGB10_A2
		case "rg11b10ufloat":
			return gl.R11F_G11F_B10F

		// Extension formats
		// TODO: It might be better to throw an error here if unorm16 is not supported
		case "r16unorm":
			return ext ? (ext.R16_EXT as number) : gl.R16UI
		case "rg16unorm":
			return ext ? (ext.RG16_EXT as number) : gl.RG16UI
		case "rgba16unorm":
			return ext ? (ext.RGBA16_EXT as number) : gl.RGBA16UI

		default:
			return gl.RGBA
	}
}

export function getTextureFormat(
	gl: WebGL2RenderingContext,
	format: TextureFormat = "rgba8unorm",
): GLenum {
	switch (format) {
		case "r8unorm":
		case "r8snorm":
		case "r16float":
		case "r32float":
			return gl.RED

		case "r8uint":
		case "r8sint":
		case "r16uint":
		case "r16sint":
		case "r32uint":
		case "r32sint":
			return gl.RED_INTEGER

		case "rg8unorm":
		case "rg8snorm":
		case "rg16float":
		case "rg32float":
			return gl.RG

		case "rg8uint":
		case "rg8sint":
		case "rg16uint":
		case "rg16sint":
		case "rg32uint":
		case "rg32sint":
			return gl.RG_INTEGER

		case "rgb8unorm":
		case "rgb8unorm-srgb":
		case "rgb8snorm":
		case "rgb16float":
		case "rgb32float":
			return gl.RGB

		case "rgb8uint":
		case "rgb8sint":
		case "rgb16uint":
		case "rgb16sint":
		case "rgb32uint":
		case "rgb32sint":
			return gl.RGB_INTEGER

		case "rgba8unorm":
		case "rgba8unorm-srgb":
		case "rgba8snorm":
		case "rgba16float":
		case "rgba32float":
			return gl.RGBA

		case "rgba8uint":
		case "rgba8sint":
		case "rgba16uint":
		case "rgba16sint":
		case "rgba32uint":
		case "rgba32sint":
			return gl.RGBA_INTEGER

		case "rgb9e5ufloat":
			return gl.RGB
		case "rgb10a2uint":
			return gl.RGBA
		case "rgb10a2unorm":
			return gl.RGBA
		case "rg11b10ufloat":
			return gl.RGB

		// Extension formats
		case "r16unorm":
			return gl.RED
		case "rg16unorm":
			return gl.RG
		case "rgba16unorm":
			return gl.RGBA

		default:
			return gl.RGBA
	}
}

export function getTextureType(
	gl: WebGL2RenderingContext,
	format: TextureFormat = "rgba8unorm",
): GLenum {
	switch (format) {
		case "r8unorm":
		case "rg8unorm":
		case "rgb8unorm":
		case "rgb8unorm-srgb":
		case "rgba8unorm":
		case "rgba8unorm-srgb":
			return gl.UNSIGNED_BYTE

		case "r8snorm":
		case "rg8snorm":
		case "rgb8snorm":
		case "rgba8snorm":
			return gl.BYTE

		case "r8uint":
		case "rg8uint":
		case "rgb8uint":
		case "rgba8uint":
			return gl.UNSIGNED_BYTE

		case "r8sint":
		case "rg8sint":
		case "rgb8sint":
		case "rgba8sint":
			return gl.BYTE

		case "r16uint":
		case "rg16uint":
		case "rgb16uint":
		case "rgba16uint":
			return gl.UNSIGNED_SHORT

		case "r16sint":
		case "rg16sint":
		case "rgb16sint":
		case "rgba16sint":
			return gl.SHORT

		case "r16float":
		case "rg16float":
		case "rgb16float":
		case "rgba16float":
			return gl.HALF_FLOAT

		case "r32uint":
		case "rg32uint":
		case "rgb32uint":
		case "rgba32uint":
			return gl.UNSIGNED_INT

		case "r32sint":
		case "rg32sint":
		case "rgb32sint":
		case "rgba32sint":
			return gl.INT

		case "r32float":
		case "rg32float":
		case "rgb32float":
		case "rgba32float":
			return gl.FLOAT

		case "rgb9e5ufloat":
		case "rg11b10ufloat":
			return gl.FLOAT

		case "rgb10a2uint":
		case "rgb10a2unorm":
			return gl.UNSIGNED_INT_2_10_10_10_REV

		// Extension formats
		case "r16unorm":
		case "rg16unorm":
		case "rgba16unorm":
			return gl.UNSIGNED_SHORT

		default:
			return gl.UNSIGNED_BYTE
	}
}
